// Pré-visualização LOCAL da regra (espec. 8.3, passo 3): a série do alvo na origem contra
// o novo cronograma que a regra produz — o valor global redistribuído a partir do início. Roda no navegador com o mesmo
// `previewTargetCurve` do motor — nada de ida ao servidor a cada tecla.
import { useEffect, useMemo, useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { useProjectContext } from "@contexts/projects";
import { formatNumberToLocale } from "@utils/formatNumber";

import { previewTargetCurve } from "./engine";
import { competenceText } from "./format";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export const NO_PLANNED_TEXT = "Este alvo não tem previsto no período; a regra fica guardada para quando houver.";
export const INVALID_PROFILE_TEXT = "Complete a forma de desembolso para ver a pré-visualização.";
export const LOADING_PLANNED_TEXT = "Carregando o previsto…";
export const PLANNED_ERROR_TEXT = "Não foi possível carregar o previsto para pré-visualizar.";
export const HIDDEN_TABLE_CAPTION = "Projeção do item por mês";

// `plannedSeries` canônico é `[{ month, cents }]` (ver `normalizeTarget`); `competence`
// é aceito por tolerância porque é o nome que a rota usa no payload cru.
const monthOf = (point) => point?.month ?? point?.competence ?? null;

const normalizePoints = (plannedSeries) => (
  Array.isArray(plannedSeries) ? plannedSeries : []
).filter((point) => monthOf(point) && Number.isFinite(point?.cents));

/**
 * Tabela mês a mês do item: a base (origem), o desembolso resultante e a diferença, com o
 * total no rodapé. Rola dentro de si — um item de 36 meses não estica o editor.
 */
const ProjectionTable = ({ rows, baseLabel, formatValue, stale }) => {
  const totals = rows.reduce(
    (sum, row) => ({ planned: sum.planned + row.planned, resulting: sum.resulting + row.resulting }),
    { planned: 0, resulting: 0 },
  );
  return (
    <div
      className={`ampli-rule-projection${stale ? " ampli-rule-preview--stale" : ""}`}
      role="region"
      aria-label={HIDDEN_TABLE_CAPTION}
      tabIndex={0}
    >
      <table>
        <caption className="visually-hidden">{HIDDEN_TABLE_CAPTION}</caption>
        <thead>
          <tr>
            <th scope="col">Mês</th>
            <th scope="col" className="num">{baseLabel}</th>
            <th scope="col" className="num">Resultante</th>
            <th scope="col" className="num">Diferença</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const delta = row.resulting - row.planned;
            return (
              <tr key={row.month} className={delta !== 0 ? "is-changed" : undefined}>
                <th scope="row">{row.label}</th>
                <td className="num">{formatValue(row.planned)}</td>
                <td className="num">{formatValue(row.resulting)}</td>
                <td className={`num${delta > 0 ? " is-up" : delta < 0 ? " is-down" : ""}`}>
                  {delta === 0 ? "—" : `${delta > 0 ? "+" : "−"}${formatValue(Math.abs(delta))}`}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td className="num">{formatValue(totals.planned)}</td>
            <td className="num">{formatValue(totals.resulting)}</td>
            <td className="num">—</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// `originName` nomeia a série de comparação quando a obra tem origem do desembolso ("Desembolso
// do ERP"): sobre uma origem que já é desembolso, "Previsto à vista" mentiria. Sem origem
// conhecida, o rótulo antigo.
const RulePreviewChart = ({
  plannedSeries = [],
  profile,
  hydration = "ready",
  originName = null,
  // O item ajustado: o gráfico e a tabela são SÓ dele, e o título diz qual é.
  targetLabel = null,
  // Início do novo cronograma; ausente, o primeiro mês do alvo na origem (como o motor).
  startMonth = null,
  // A série resultante do alvo calculada pelo servidor (`[[mês, centavos]]`, D19). Vem
  // quando há insumos com regra própria no alvo: a prévia local não conhece a forma deles,
  // então o resultante é o do motor, no cenário que o usuário escolheu.
  resultingSeries = null,
  // O nome da série resultante ("Ajustado" no ajuste mês a mês) e se a tabela mês a mês do
  // próprio gráfico aparece — o ajuste mês a mês já tem a sua, editável.
  resultingLabel = "Desembolso resultante",
  showTable = true,
}) => {
  const baseLabel = originName ?? "Previsto à vista";
  /**
   * Os callbacks do chart.js (ticks/tooltip) rodam FORA do render do React, então
   * `formatNumberToCurrency` — que chama `useProjectContext` por dentro — explodiria
   * ali com "Invalid hook call". Padrão do `ExtraValuesMonthlyChart`: ler o contexto
   * AQUI, no corpo do componente, e fechar sobre um formatador PURO.
   */
  const { cubBased, activeProject } = useProjectContext();
  const indexerValue = activeProject?.indexer?.value;
  const unitLabel = cubBased ? "(CUB)" : "(R$)";
  const heading = `${targetLabel ? `${targetLabel} — ` : ""}${baseLabel} × ${resultingLabel} ${unitLabel}`;
  const formatValue = (value) => {
    const safe = Number(value) || 0;
    if (cubBased) {
      return formatNumberToLocale(safe / (indexerValue && indexerValue !== 0 ? indexerValue : 1));
    }
    return safe.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const points = useMemo(() => normalizePoints(plannedSeries), [plannedSeries]);

  // Três estados possíveis do par (previsto, forma), sempre explícitos: desenho
  // pronto, forma que o motor recusa, ou alvo sem previsto no período. Os estados
  // da BUSCA do previsto vêm de fora, em `hydration`.
  const state = useMemo(() => {
    let rows;
    try {
      rows = previewTargetCurve({
        plannedSeries: points.map((point) => ({ competence: monthOf(point), cents: point.cents })),
        profile,
        startMonth,
      });
    } catch {
      return { kind: "invalid", chart: null, rows: null };
    }
    if (points.length === 0) return { kind: "no_planned", chart: null, rows: null };
    const planned = new Map(points.map((point) => [monthOf(point), point.cents]));
    const resulting = Array.isArray(resultingSeries)
      ? new Map(resultingSeries)
      : new Map(rows.map((row) => [row.competence, row.cents]));
    const months = [...new Set([...planned.keys(), ...resulting.keys()])].sort();
    return {
      kind: "ok",
      rows: months.map((month) => ({
        month,
        label: competenceText(month),
        planned: (planned.get(month) ?? 0) / 100,
        resulting: (resulting.get(month) ?? 0) / 100,
      })),
      chart: {
        labels: months.map(competenceText),
        datasets: [
          {
            label: baseLabel,
            backgroundColor: "#ba52f2",
            borderColor: "#ba52f2",
            data: months.map((month) => (planned.get(month) ?? 0) / 100),
          },
          {
            label: resultingLabel,
            backgroundColor: "#245AE4",
            borderColor: "#245AE4",
            data: months.map((month) => (resulting.get(month) ?? 0) / 100),
          },
        ],
      },
    };
  }, [points, profile, startMonth, baseLabel, resultingSeries, resultingLabel]);

  // Perfil incompleto no meio da digitação não pode piscar a tela para vazio: o
  // último desenho VÁLIDO fica no ar — apagado, para não passar por resposta do
  // motor ao que está digitado agora.
  const lastValid = useRef(null);
  useEffect(() => {
    if (state.chart) lastValid.current = { chart: state.chart, rows: state.rows };
  }, [state]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      // A plataforma registra `chartjs-plugin-datalabels` globalmente, que desenharia
      // o valor cru (em reais, sem CUB) dentro da barra.
      datalabels: { display: false },
      legend: { position: "bottom" },
      tooltip: {
        callbacks: {
          label: (item) => `${item.dataset.label}: ${formatValue(item.raw)}`,
        },
      },
    },
    scales: {
      y: { ticks: { callback: (value) => formatValue(value) } },
    },
    // `formatValue` fecha sobre o modo da obra: mudar de R$ para CUB tem que refazer
    // as opções, senão o gráfico segue formatando na unidade antiga.
  }), [cubBased, indexerValue]);

  if (hydration === "loading") {
    // Nunca "não tem previsto" enquanto a série está a caminho: seria um veredito
    // sobre o alvo baseado em dado que ainda não chegou.
    return (
      <>
        <h3>{heading}</h3>
        <p className="card-label">{LOADING_PLANNED_TEXT}</p>
      </>
    );
  }
  if (hydration === "failed") {
    return (
      <>
        <h3>{heading}</h3>
        <p className="alert alert-error" role="alert">{PLANNED_ERROR_TEXT}</p>
      </>
    );
  }

  const shown = state.chart ? { chart: state.chart, rows: state.rows } : lastValid.current;
  const stale = !state.chart && !!shown;
  const rows = shown?.rows ?? [];
  const summary = rows.length
    ? `${baseLabel} e ${resultingLabel.toLowerCase()} em ${rows.length} ${
      rows.length === 1 ? "mês" : "meses"
    }, de ${rows[0].label} a ${rows[rows.length - 1].label}, em ${cubBased ? "CUB" : "R$"}.`
    : heading;

  const resultingRows = rows.filter((row) => row.resulting !== 0);
  const adjustedTotal = rows.reduce((total, row) => total + row.planned, 0);
  const graph = shown ? (
    <>
      <p className="ampli-rule-preview__facts" data-testid="rule-preview-facts">
        <span>
          Valor global <strong>{formatValue(adjustedTotal)}</strong>
        </span>
        {resultingRows.length > 0 ? (
          <span>
            {"Projeção de "}
            <strong>{resultingRows[0].label}</strong>
            {" a "}
            <strong>{resultingRows[resultingRows.length - 1].label}</strong>
            {` (${resultingRows.length} ${resultingRows.length === 1 ? "mês" : "meses"})`}
          </span>
        ) : null}
      </p>
      <div
        className={`ampli-rule-preview${stale ? " ampli-rule-preview--stale" : ""}`}
        style={{ height: "260px" }}
        role="group"
        aria-label={summary}
      >
        <Bar data={shown.chart} options={options} />
      </div>
      {/* A projeção do item por mês, à vista (não só para leitor de tela): é ela que diz
          QUANDO cada parte do valor ajustado cai e quanto muda em cada mês. */}
      {showTable ? (
        <ProjectionTable
          rows={rows}
          baseLabel={originName ?? "Previsto"}
          formatValue={formatValue}
          stale={stale}
        />
      ) : null}
    </>
  ) : null;

  if (state.kind === "no_planned") {
    // Nunca uma curva de zeros: o cliente leria zero como resposta do motor.
    return (
      <>
        <h3>{heading}</h3>
        <p className="empty-state">{NO_PLANNED_TEXT}</p>
      </>
    );
  }
  if (state.kind === "invalid") {
    return (
      <>
        <h3>{heading}</h3>
        {graph}
        <p className="empty-state" role="status">{INVALID_PROFILE_TEXT}</p>
      </>
    );
  }
  return (
    <>
      <h3>{heading}</h3>
      {graph}
    </>
  );
};

export default RulePreviewChart;
